import React, { useState, useEffect, useRef } from "react";
import { apiClient, baseURL } from "../utils/const";
import { toast } from "react-toastify";

const GOLD_FIELDS = [
  "L_1_gm", "L_2_gm", "L_5_gm", "L_10_gm", "L_20_gm", "L_50_gm",
  "1_gm", "2_gm", "5_gm", "10_gm", "20_gm", "50_gm",
];

const SILVER_FIELDS = [
  "5_gm", "10_gm", "20_gm", "25_gm", "50_gm",
  "100_gm", "250_gm", "500_gm", "1000_gm",
  "ggc_5_gm", "ggc_10_gm", "ggc_20_gm", "ggc_25_gm", "ggc_50_gm",
  "ggc_100_gm", "ggc_250_gm", "ggc_500_gm", "ggc_1000_gm",
];

const GOLD_LOCAL = GOLD_FIELDS.filter((f) => f.startsWith("L_"));
const GOLD_GGC = GOLD_FIELDS.filter((f) => !f.startsWith("L_"));
const SILVER_LOCAL = SILVER_FIELDS.filter((f) => !f.startsWith("ggc_"));
const SILVER_GGC = SILVER_FIELDS.filter((f) => f.startsWith("ggc_"));

const getFieldLabel = (field, metal = "gold") => {
  if (field.startsWith("L_")) {
    return field.replace(/^L_/, "").replace(/_/g, " ");
  }
  if (field.startsWith("ggc_")) {
    return `GGC ${field.replace("ggc_", "").replace(/_/g, " ")}`;
  }
  // Gold plain keys (1_gm…) are GGC; silver plain keys are local
  if (metal === "gold") return `GGC ${field.replace(/_/g, " ")}`;
  return field.replace(/_/g, " ");
};

const emptyGoldForm = () => ({
  page_title: "Gold Live Rate",
  status: "active",
  xindex: "",
  include: "",
  markup: "",
  ...Object.fromEntries(GOLD_FIELDS.map((f) => [f, ""])),
});

const emptySilverForm = () => ({
  page_title: "Silver Live Rate",
  status: "active",
  xindex: "",
  include: "",
  markup: "",
  ...Object.fromEntries(SILVER_FIELDS.map((f) => [f, ""])),
});

const GET_URL = `${baseURL}/user_api/get_live_rate.php`;
const INSERT_URL = `${baseURL}/user_api/insert_live_rate.php`;

let inflightGet = null;
let inflightInsert = null;

const BRAND = "#4B2C3B";
const GOLD = "#D3AF37";
const SUBMIT = "#dc3545";

const LivePrice = () => {
  const [metalType, setMetalType] = useState("gold");
  const [formData, setFormData] = useState(emptyGoldForm);
  const [silverFormData, setSilverFormData] = useState(emptySilverForm);
  const [currentPrice, setCurrentPrice] = useState(null);
  const [currentSilverPrice, setCurrentSilverPrice] = useState(null);
  const [liveRates, setLiveRates] = useState([]);
  const [silverRates, setSilverRates] = useState([]);
  const [infoData, setInfoData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const submittingRef = useRef(false);

  useEffect(() => {
    fetchLiveRates();
  }, []);

  useEffect(() => {
    if (!currentPrice) return;
    setFormData({
      page_title: currentPrice.page_title || "Gold Live Rate",
      status: Number(currentPrice.isActive) === 0 ? "active" : "inactive",
      xindex: String(
        currentPrice.xindex ?? infoData?.gold_priceIndex ?? infoData?.line ?? ""
      ),
      include: String(currentPrice.include ?? 0),
      markup: String(currentPrice.markup ?? infoData?.gold_markup ?? 0),
      ...Object.fromEntries(GOLD_FIELDS.map((f) => [f, currentPrice[f] || ""])),
    });
  }, [currentPrice, infoData]);

  useEffect(() => {
    if (!currentSilverPrice) return;
    setSilverFormData({
      page_title: currentSilverPrice.name || "Silver Live Rate",
      status: Number(currentSilverPrice.isActive) === 0 ? "active" : "inactive",
      xindex: String(
        currentSilverPrice.xindex ?? infoData?.silver_priceIndex ?? ""
      ),
      include: String(currentSilverPrice.include ?? 0),
      markup: String(
        currentSilverPrice.markup ?? infoData?.silver_markup ?? 0
      ),
      ...Object.fromEntries(
        SILVER_FIELDS.map((f) => [f, currentSilverPrice[f] || ""])
      ),
    });
  }, [currentSilverPrice, infoData]);

  const fetchLiveRates = async () => {
    if (inflightGet) return inflightGet;

    inflightGet = (async () => {
      try {
        const response = await apiClient.post(GET_URL);
        if (response.data.message !== "radha") return;

        const data = response.data.data || {};
        const silverAll = Array.isArray(data.silver_all)
          ? data.silver_all
          : Array.isArray(data.silver_rates)
            ? data.silver_rates
            : [];

        setCurrentPrice(data.latest || data.gold_rate_info || null);
        setLiveRates(Array.isArray(data.all) ? data.all : []);
        setCurrentSilverPrice(
          data.silver_rate_info || data.silver_latest || silverAll[0] || null
        );
        setSilverRates(silverAll);
        setInfoData(data.info_data || null);
      } catch (err) {
        console.error("Error fetching live rates:", err);
      } finally {
        inflightGet = null;
      }
    })();

    return inflightGet;
  };

  const insertLiveRate = async (payload) => {
    if (inflightInsert) return inflightInsert;

    inflightInsert = apiClient
      .post(INSERT_URL, payload)
      .finally(() => {
        inflightInsert = null;
      });

    return inflightInsert;
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSilverInputChange = (e) => {
    const { name, value } = e.target;
    setSilverFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleGoldSubmit = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (submittingRef.current || loading || inflightInsert) return;
    submittingRef.current = true;
    setLoading(true);
    setError(null);

    try {
      if (!formData.page_title?.trim()) {
        setError("Please enter page title before submitting.");
        return;
      }
      if (["xindex", "include", "markup"].some((f) => formData[f] === "" || formData[f] == null)) {
        setError("Please fill xindex, include and markup before submitting.");
        return;
      }
      if (GOLD_FIELDS.some((f) => formData[f] === "" || formData[f] == null)) {
        setError("Please fill all local and GGC gold rate fields before submitting.");
        return;
      }

      const payload = {
        rate_type: "gold",
        page_title: formData.page_title.trim(),
        isActive: formData.status === "active" ? 0 : 1,
        xindex: Number(formData.xindex),
        include: Number(formData.include),
        markup: Number(formData.markup),
        ...Object.fromEntries(GOLD_FIELDS.map((f) => [f, Number(formData[f])])),
      };

      const response = await insertLiveRate(payload);

      if (response.data.message === "radha") {
        toast.success("Live gold price added successfully!");
        setFormData(emptyGoldForm());
        await fetchLiveRates();
      } else {
        setError(response.data.message || "Failed to add live price");
      }
    } catch (err) {
      console.error("Error adding live price:", err);
      setError(err.response?.data?.message || err.message || "Failed to add live price");
    } finally {
      submittingRef.current = false;
      setLoading(false);
    }
  };

  const handleSilverSubmit = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (submittingRef.current || loading || inflightInsert) return;
    submittingRef.current = true;
    setLoading(true);
    setError(null);

    try {
      if (!silverFormData.page_title?.trim()) {
        setError("Please enter page title before submitting.");
        return;
      }
      if (
        ["xindex", "include", "markup"].some(
          (f) => silverFormData[f] === "" || silverFormData[f] == null
        )
      ) {
        setError("Please fill xindex, include and markup before submitting.");
        return;
      }
      if (SILVER_FIELDS.some((f) => silverFormData[f] === "" || silverFormData[f] == null)) {
        setError("Please fill all silver rate fields before submitting.");
        return;
      }

      const payload = {
        rate_type: "silver",
        name: silverFormData.page_title.trim() || "Silver Live Rate",
        xindex: String(silverFormData.xindex),
        include: String(silverFormData.include),
        markup: String(silverFormData.markup),
        isActive: silverFormData.status === "active" ? 0 : 1,
        ...Object.fromEntries(
          SILVER_FIELDS.map((f) => [f, String(silverFormData[f])])
        ),
      };

      const response = await insertLiveRate(payload);

      if (response.data.message === "radha") {
        toast.success("Live silver price added successfully!");
        setSilverFormData(emptySilverForm());
        await fetchLiveRates();
      } else {
        setError(response.data.message || "Failed to add live silver price");
      }
    } catch (err) {
      console.error("Error adding live silver price:", err);
      setError(
        err.response?.data?.message || err.message || "Failed to add live silver price"
      );
    } finally {
      submittingRef.current = false;
      setLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    if (amount == null || amount === "" || amount === "—") return "₹ —";
    const num = Number(amount);
    if (isNaN(num)) return "₹ —";
    return `₹${num.toLocaleString("en-IN")}`;
  };

  const formatDate = (dateString) => {
    if (!dateString) return "—";
    return new Date(dateString).toISOString().split("T")[0];
  };

  const isGold = metalType === "gold";
  const rateFields = isGold ? GOLD_FIELDS : SILVER_FIELDS;
  const localFields = isGold ? GOLD_LOCAL : SILVER_LOCAL;
  const ggcFields = isGold ? GOLD_GGC : SILVER_GGC;
  const latest = isGold ? currentPrice : currentSilverPrice;
  const history = isGold ? liveRates : silverRates;
  const onChange = isGold ? handleInputChange : handleSilverInputChange;
  const getVal = (name) => (isGold ? formData[name] : silverFormData[name]);

  const softTiles = [
    "#F8F4EC",
    "#F3F6F8",
    "#F5F2F7",
    "#F7F6F2",
    "#F2F7F5",
  ];

  const cardStyle = {
    background: "var(--bs-body-bg)",
    borderRadius: 14,
    boxShadow: "0 4px 14px rgba(0,0,0,0.04)",
    border: "1px solid rgba(0,0,0,0.04)",
    padding: 16,
    marginBottom: 14,
  };

  const fieldWrap = { display: "flex", flexDirection: "column", gap: 4, flex: "0 0 auto" };
  const labelStyle = {
    fontSize: 11,
    color: "var(--bs-secondary-color)",
    whiteSpace: "nowrap",
  };
  const inputStyle = {
    width: 110,
    borderRadius: 8,
    border: "1px solid #d8dee6",
    padding: "7px 10px",
    fontSize: 13,
    background: "var(--bs-body-bg)",
    color: "var(--bs-body-color)",
  };

  const groupTitle = {
    fontSize: 12,
    fontWeight: 600,
    color: "var(--bs-secondary-color)",
    marginBottom: 8,
  };

  const renderField = (name, opts = {}) => (
    <div key={name} style={fieldWrap}>
      <label style={labelStyle}>{opts.label || getFieldLabel(name, metalType)}</label>
      <input
        type={opts.type || "number"}
        name={name}
        value={getVal(name)}
        onChange={onChange}
        placeholder={
          opts.placeholder ||
          (latest?.[name] != null && latest[name] !== ""
            ? `Last: ${latest[name]}`
            : opts.fallback || "")
        }
        className="form-control"
        style={{ ...inputStyle, width: opts.width || 110 }}
        step={opts.type === "text" ? undefined : "0.01"}
      />
    </div>
  );

  const renderPriceTiles = (items, startIndex = 0) => (
    <div
      style={{
        display: "grid",
        gridTemplateColumns: "repeat(auto-fill, minmax(110px, 1fr))",
        gap: 10,
      }}
    >
      {items.map((item, index) => (
        <div
          key={item.label}
          style={{
            background: softTiles[(startIndex + index) % softTiles.length],
            borderRadius: 10,
            padding: "12px 10px",
            textAlign: "center",
          }}
        >
          <div
            style={{
              fontSize: 11,
              fontWeight: 600,
              color: "var(--bs-secondary-color)",
              marginBottom: 6,
            }}
          >
            {item.label}
          </div>
          <div style={{ fontSize: 14, fontWeight: 700, color: "#334155" }}>
            {item.value}
          </div>
        </div>
      ))}
    </div>
  );

  const metaTiles = [{ label: "Date", value: formatDate(latest?.date) }];

  const localTiles = localFields.map((field) => ({
    label: getFieldLabel(field, metalType),
    value: formatCurrency(latest?.[field]),
  }));

  const ggcTiles = ggcFields.map((field) => ({
    label: getFieldLabel(field, metalType),
    value: formatCurrency(latest?.[field]),
  }));

  return (
    <div className="page-wrapper">
      <div className="page-content">
        <div className="container-xxl">
          {/* Header */}
          <div
            className="d-flex justify-content-between align-items-center flex-wrap gap-2"
            style={{ ...cardStyle, padding: "12px 16px" }}
          >
            <h2 className="mb-0 fw-bold" style={{ fontSize: "1.25rem", color: "var(--bs-body-color)" }}>
              Live {isGold ? "Gold" : "Silver"} • Price
            </h2>

            <div
              style={{
                display: "inline-flex",
                padding: 3,
                borderRadius: 999,
                background: "rgba(75,44,59,0.05)",
                border: "1px solid rgba(75,44,59,0.12)",
              }}
            >
              {[
                { key: "gold", label: "Gold", bg: `linear-gradient(135deg, ${GOLD}, #B8941F)` },
                { key: "silver", label: "Silver", bg: "linear-gradient(135deg, #C0C0C0, #94a3b8)" },
              ].map((opt) => (
                <button
                  key={opt.key}
                  type="button"
                  onClick={() => {
                    setError(null);
                    setMetalType(opt.key);
                  }}
                  style={{
                    border: "none",
                    borderRadius: 999,
                    padding: "6px 16px",
                    fontSize: 13,
                    fontWeight: 600,
                    cursor: "pointer",
                    background: metalType === opt.key ? opt.bg : "transparent",
                    color: metalType === opt.key ? BRAND : "var(--bs-secondary-color)",
                  }}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          </div>

          {/* Add Live Price */}
          <div style={cardStyle}>
            <div style={{ fontWeight: 600, marginBottom: 12, color: "var(--bs-body-color)" }}>
              Add Live Price
            </div>

            <form onSubmit={isGold ? handleGoldSubmit : handleSilverSubmit}>
              <div
                style={{
                  display: "flex",
                  gap: 10,
                  alignItems: "flex-end",
                  overflowX: "auto",
                  paddingBottom: 4,
                  marginBottom: 12,
                }}
              >
                {[
                  { name: "xindex", fallback: isGold ? "2148" : "0" },
                  { name: "include", fallback: isGold ? "5" : "0" },
                  { name: "markup", fallback: "0" },
                ].map((f) =>
                  renderField(f.name, {
                    label: f.name,
                    fallback: f.fallback,
                    width: 100,
                    placeholder:
                      latest?.[f.name] != null
                        ? `Last: ${latest[f.name]}`
                        : f.fallback,
                  })
                )}

                {renderField("page_title", {
                  type: "text",
                  label: "Page Title",
                  width: 150,
                  placeholder: isGold
                    ? currentPrice?.page_title
                      ? `Last: ${currentPrice.page_title}`
                      : "Gold Live Rate"
                    : currentSilverPrice?.name
                      ? `Last: ${currentSilverPrice.name}`
                      : "Silver Live Rate",
                })}

                <div style={fieldWrap}>
                  <label style={labelStyle}>Status</label>
                  <div style={{ display: "flex", gap: 10, height: 36, alignItems: "center" }}>
                    {["active", "inactive"].map((value) => (
                      <label
                        key={value}
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: 4,
                          fontSize: 12,
                          textTransform: "capitalize",
                          cursor: "pointer",
                          whiteSpace: "nowrap",
                        }}
                      >
                        <input
                          type="radio"
                          name="status"
                          value={value}
                          checked={getVal("status") === value}
                          onChange={onChange}
                          style={{ accentColor: BRAND }}
                        />
                        {value}
                      </label>
                    ))}
                  </div>
                </div>
              </div>

              <div
                style={{
                  display: "flex",
                  gap: 10,
                  alignItems: "flex-end",
                  overflowX: "auto",
                  paddingBottom: 4,
                  marginBottom: 12,
                }}
              >
                {localFields.map((f) => renderField(f))}
              </div>

              <div
                style={{
                  display: "flex",
                  gap: 10,
                  alignItems: "flex-end",
                  overflowX: "auto",
                  paddingBottom: 4,
                  marginBottom: 14,
                }}
              >
                {ggcFields.map((f) => renderField(f))}
                <button
                  type="submit"
                  disabled={loading}
                  className="btn"
                  style={{
                    backgroundColor: SUBMIT,
                    height: 36,
                    borderRadius: 8,
                    padding: "6px 16px",
                    color: "white",
                    border: "none",
                    fontSize: 13,
                    fontWeight: 600,
                    opacity: loading ? 0.7 : 1,
                    flex: "0 0 auto",
                    marginBottom: 1,
                  }}
                >
                  {loading ? "Submitting..." : "Submit"}
                </button>
              </div>

              {error && (
                <div className="alert alert-danger mb-0 py-2" role="alert" style={{ fontSize: 12 }}>
                  {error}
                </div>
              )}
            </form>
          </div>

          {/* Current Price */}
          <div style={cardStyle}>
            <div style={{ fontWeight: 600, marginBottom: 12, color: "var(--bs-body-color)" }}>
              Current Price (Last Entry)
            </div>

            {renderPriceTiles([...metaTiles, ...localTiles], 0)}
            <div style={{ marginTop: 12 }}>
              <div style={{ ...groupTitle, marginBottom: 8 }}>GGC rates</div>
              {renderPriceTiles(ggcTiles, metaTiles.length + localTiles.length)}
            </div>
          </div>

          {/* Live Rates */}
          <div style={{ ...cardStyle, padding: 0, overflow: "hidden" }}>
            <div style={{ padding: "12px 16px", borderBottom: "1px solid rgba(0,0,0,0.05)" }}>
              <div style={{ fontWeight: 600, color: "var(--bs-body-color)" }}>Live Rates</div>
            </div>

            <div style={{ maxHeight: 260, overflowX: "auto", overflowY: "auto" }}>
              {history.length > 0 ? (
                <table
                  className="table table-hover mb-0"
                  style={{
                    minWidth: isGold ? 1400 : 2100,
                    fontSize: 13,
                    whiteSpace: "nowrap",
                  }}
                >
                  <thead
                    className="table-light"
                    style={{ position: "sticky", top: 0, zIndex: 10 }}
                  >
                    <tr>
                      <th style={{ whiteSpace: "nowrap", padding: "10px 14px" }}>Date</th>
                      <th style={{ whiteSpace: "nowrap", padding: "10px 14px" }}>Admin Name</th>
                      <th style={{ whiteSpace: "nowrap", padding: "10px 14px" }}>Page Title</th>
                      {rateFields.map((f) => (
                        <th key={f} style={{ whiteSpace: "nowrap", padding: "10px 14px" }}>
                          {getFieldLabel(f, metalType).toUpperCase()}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {history.map((rate, index) => (
                      <tr key={index}>
                        <td style={{ whiteSpace: "nowrap", padding: "10px 14px" }}>
                          {formatDate(rate.date)}
                        </td>
                        <td style={{ whiteSpace: "nowrap", padding: "10px 14px" }}>
                          {rate.admin_name || "—"}
                        </td>
                        <td style={{ whiteSpace: "nowrap", padding: "10px 14px" }}>
                          {isGold
                            ? rate.page_title || "—"
                            : rate.name || "Silver Live Rate"}
                        </td>
                        {rateFields.map((f) => (
                          <td key={f} style={{ whiteSpace: "nowrap", padding: "10px 14px" }}>
                            {formatCurrency(rate[f])}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              ) : (
                <div className="text-center py-5">
                  <i className="fas fa-chart-line text-muted fs-1 mb-3" />
                  <p className="text-muted mb-0">No rates found</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LivePrice;
